import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { supabase } from '../utils/supabaseClient';

const AuthContext = createContext({
	user: null,
	session: null,
	profile: null,
	loading: false,
	error: null,
});

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [session, setSession] = useState(null);
	const [profile, setProfile] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// 1️⃣ Initial session fetch + auth listener
	useEffect(() => {
		// Get initial session — must complete before we allow loading=false so AppLayout doesn't redirect to login too early
		supabase.auth.getSession().then(({ data }) => {
			setSession(data.session);
			setUser(data.session?.user ?? null);
			// Only mark "not loading" when we know there's no session; if there is a session, second useEffect will set loading false after profile fetch
			if (!data.session) {
				setLoading(false);
			}
		});

		// Subscribe to auth state changes
		const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
			setSession(session);
			setUser(session?.user ?? null);
		});

		// Cleanup
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	// 2️⃣ Fetch full user profile once session is ready
// AuthContext.js (Updated second useEffect)

	useEffect(() => {
		// If there's no session, reset profile and errors. Do NOT set loading false here —
		// the initial getSession() in the first useEffect does that when it resolves with no session.
		// Otherwise we'd set loading false before getSession() runs and trigger a redirect loop.
		if (!session) {
			setProfile(null);
			setError(null);
			return;
		}

		// If session exists, proceed to fetch profile data
		setLoading(true);
		setError(null);

		fetch(`${process.env.REACT_APP_API_BASE_URL}/fetch_user_data`, {
			headers: {
				Authorization: `Bearer ${session.access_token}`,
			},
		})
			.then((res) => {
				if (!res.ok) {
					// Attempt to get error message from response if possible
					return res.text().then(text => {
						try {
							const errData = JSON.parse(text);
							throw new Error(errData.message || `Failed to fetch user profile (${res.status})`);
						} catch (e) {
							throw new Error(text || `Failed to fetch user profile (${res.status})`);
						}
					});
				}
				return res.json();
			})
			.then((data) => setProfile(data))
			.catch((err) => {
				console.error("Error fetching user profile:", err); // Log the actual error
				setError(err); // Set the error state
				setProfile(null); // Clear profile on error
			})
			.finally(() => {
				setLoading(false); // Set loading false after fetch completes (success or error)
			});

	}, [session]); // Dependency remains session

	useEffect(() => {
		if (profile) {
			console.log('✅ Latest user profile:', profile);
		}
	}, [profile]);

	const resetAuth = () => {
		setUser(null);
		setSession(null);
		setProfile(null);
		setLoading(false);
		setError(null);
	};


	// 3️⃣ Memoized context
	const value = useMemo(
		() => ({ user, session, profile, loading, error, resetAuth }),
		[user, session, profile, loading, error]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	return useContext(AuthContext);
}
